import { useState, useEffect, useRef } from "react";
import ChatBotLauncher from "./ChatBotLauncher";
import ChatBotExpanded from "./ChatBotExpanded";
import { sendCollectionsMessage, getSessionId } from "./ActionCreator";
import ChatBotDrawer from "./ChatBotDrawer";
import { connectChatSocket } from "../AIFinal/webshocket/chatSocket";
import { startTypingEffect } from "../AIFinal/Utils/typingEffect";

import {
  fetchConversationList,
  fetchConversationHistory,
  checkHealth,
} from "./ActionCreator";

const MODES = {
  COLLECTION: "collection",
  BUSINESS: "business",
};

export default function ChatBot({ pageViewHandler, pageState }) {
  const [expanded, setExpanded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [msg, setMsg] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [followUpRef, setFollowUpRef] = useState(null);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [mode, setMode] = useState(MODES.COLLECTION);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [currentMessages, setCurrentMessages] = useState([]);
  const [hasTyped, setHasTyped] = useState(false);
  const chatsLoadedRef = useRef(false);
  const processingCompleteRef = useRef(false);
  const activeChat = chats.find((c) => c.id === activeChatId);

  useEffect(() => {
    if (!sessionId) return;
    connectChatSocket(sessionId);
    return () => {};
  }, [sessionId]);

  useEffect(() => {
    window.addBotStep = (stepData) => {
      if (!streamingMessageId) return;

      setChats((prev) =>
        prev.map((chat) => ({
          ...chat,
          messages: chat.messages.map((msg) => {
            if (msg.id !== streamingMessageId) return msg;

            const steps = [
              ...(msg.intermediateSteps || []),
              {
                title: stepData.event || "Processing",
                description: stepData.data || "",
              },
            ];

            const updatedMsg = {
              ...msg,
              intermediateSteps: steps,
              currentStepIndex: steps.length - 1,
              showSteps: true,
              isProcessing: true,
            };

            if (stepData.event === "complete" || stepData.isFinal) {
              processingCompleteRef.current = true;
              return {
                ...updatedMsg,
                fullText: stepData.finalText || stepData.data?.text || "",
                table: stepData.data?.table || null,
                sqlQuery: stepData.data?.sql || null,
                query_id: stepData.data?.query_id || stepData.query_id || null,

                isProcessing: false,
                showSteps: true,
              };
            }

            return updatedMsg;
          }),
        }))
      );
    };

    return () => {
      delete window.addBotStep;
    };
  }, [streamingMessageId]);

  const loadChats = async () => {
    if (chatsLoadedRef.current) {
      console.log("Chats already loaded, skipping API");
      return;
    }

    try {
      console.log("LOADING CHAT LIST (ONCE)...");
      const data = await fetchConversationList("SuperUser", mode, sessionId);
      const formatted = data.map((item) => ({
        id: item.session_id,
        title: item.preview,
        messages: [],
        messagesLoaded: false,
      }));

      setChats(formatted);
      chatsLoadedRef.current = true;
    } catch (err) {
      console.error("CHAT LIST ERROR:", err);
    }
  };

  const resetChat = () => {
    setActiveChatId(null);
    setCurrentMessages([]);
    setHasTyped(false);
    setStreamingMessageId(null);
    setFollowUpRef(null);
    processingCompleteRef.current = false;
  };

  const createNewChat = () => resetChat();

  const changeMode = async (newMode, updateMode = true) => {
    if (updateMode) {
      setMode(newMode);
    }
    resetChat();

    setChats([]);
    chatsLoadedRef.current = false;

    try {
      console.log("LOADING CHAT LIST FOR MODE:", newMode);
      const data = await fetchConversationList("SuperUser", newMode, sessionId);
      const formatted = data.map((item) => ({
        id: item.session_id,
        title: item.preview,
        messages: [],
        messagesLoaded: false,
      }));

      setChats(formatted);
      chatsLoadedRef.current = true;
    } catch (err) {
      console.error("CHAT LIST ERROR:", err);
    }
  };

  const loadChat = async (chatId) => {
    setActiveChatId(chatId);
    setHasTyped(true);
    setStreamingMessageId(null);
    processingCompleteRef.current = false;

    const existingChat = chats.find((c) => c.id === chatId);
    if (
      existingChat &&
      existingChat.messagesLoaded &&
      existingChat.messages.length > 0
    ) {
      console.log("Messages already loaded for this chat");
      return;
    }

    try {
      console.log("LOADING HISTORY FOR:", chatId);
      const history = await fetchConversationHistory({
        userId: "SuperUser",
        mode,
        sessionId: chatId,
      });

      console.log("HISTORY DATA:", history);

      const formattedMessages = history.flatMap((item) => {
        let table = null;

        if (Array.isArray(item.sample_rows) && item.sample_rows.length > 0) {
          const columns = Object.keys(item.sample_rows[0]);
          const rows = item.sample_rows.map((row) =>
            columns.map((col) => row[col])
          );

          const csvDownload = item.csv_download_endpoint || null;

          table = {
            columns,
            rows,
            csvDownload,
          };
        }

        return [
          {
            id: `user-${item.id}`,
            sender: "user",
            content: item.user_input || "",
            timestamp: new Date(),
          },
          {
            id: `bot-${item.id}`,
            sender: "bot",
            type: "mixed",
            text: item.response_message || "",
            fullText: item.response_message || "",
            query_id: item.id,
            session_id: chatId,
            table,
            sqlQuery: item.sql || null,
            intermediateSteps: [],
            currentStepIndex: 0,
            showSteps: true,
            isProcessing: false,
            timestamp: new Date(),
          },
        ];
      });

      setChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId
            ? { ...chat, messages: formattedMessages, messagesLoaded: true }
            : chat
        )
      );
    } catch (err) {
      console.error("HISTORY LOAD ERROR:", err);
    }
  };

  const addMessage = (chatId, message) => {
    if (!chatId) {
      setCurrentMessages((prev) => [...prev, message]);
      return;
    }

    setChats((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              messages: [...chat.messages, message],
              messagesLoaded: true,
            }
          : chat
      )
    );
  };

  const handleSend = async (text) => {
    const message = text || msg;
    if (!message.trim() || isTyping) return;

    const userMsg = {
      id: crypto.randomUUID(),
      sender: "user",
      content: message,
      timestamp: new Date(),
    };

    let chatId = activeChatId || crypto.randomUUID();

    if (!activeChatId) {
      setChats((prev) => [
        {
          id: chatId,
          title: message.slice(0, 40),
          messages: [userMsg],
          messagesLoaded: true,
        },
        ...prev,
      ]);
      setActiveChatId(chatId);
      setHasTyped(true);
    } else {
      addMessage(chatId, userMsg);
    }

    setMsg("");
    setIsTyping(true);
    processingCompleteRef.current = false;

    const botId = crypto.randomUUID();
    setStreamingMessageId(botId);

    addMessage(chatId, {
      id: botId,
      sender: "bot",
      type: "mixed",
      text: "",
      fullText: "",
      intermediateSteps: [],
      currentStepIndex: 0,
      showSteps: true,
      isProcessing: true,
      timestamp: new Date(),
    });

    await new Promise((res) => setTimeout(res, 500));

    setTimeout(async () => {
      if (processingCompleteRef.current) {
        console.log("WS completed - skipping API");
        setIsTyping(false);
        return;
      }

      console.log("WS fallback: Calling API...");
      try {
        const result = await sendCollectionsMessage(message, mode, sessionId);
        console.log("API RESULT:", result);

        if (result?.text) {
          const fullText = result.text;

          setChats((prev) =>
            prev.map((chat) => ({
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.id === botId
                  ? {
                      ...msg,
                      fullText,
                      table: result.table || null,
                      sqlQuery: result.sql || null,
                      query_id: result.query_id,
                      isProcessing: false,
                      showSteps: true,
                    }
                  : msg
              ),
            }))
          );

          startTypingEffect({
            chatId,
            messageId: botId,
            fullText,
            setChats,
            setCurrentMessages,
            onComplete: () => {
              setStreamingMessageId(null);
              processingCompleteRef.current = true;
            },
          });
        } else {
          console.warn("Empty API response - waiting for WS final");
        }
      } catch (err) {
        console.error("SEND ERROR:", err);

        setChats((prev) =>
          prev.map((chat) => ({
            ...chat,
            messages: chat.messages.map((msg) =>
              msg.id === botId
                ? {
                    ...msg,
                    fullText:
                      "Sorry, there was an error processing your request.",
                    isProcessing: false,
                    showSteps: false,
                  }
                : msg
            ),
          }))
        );
      }
    }, 2000);

    setIsTyping(false);
  };

  const handleLauncherClick = async () => {
    const sessionId = getSessionId();
    setSessionId(sessionId);
    console.log("Setting sessionId:", sessionId);

    try {
      const health = await checkHealth();
      console.log("CHAT HEALTH STATUS:", health);

      if (health?.status !== "ok") {
        console.warn("Backend not healthy");
        return;
      }
    } catch (err) {
      console.error("Health check failed", err);
      return;
    }

    await loadChats();
    setDrawerOpen(true);
    setExpanded(false);

    if (chats.length === 0 && !hasTyped) {
      createNewChat();
    }
  };

  const displayMessages =
    activeChatId && activeChat?.messages.length > 0
      ? activeChat.messages
      : currentMessages;

  return (
    <>
      <div className="chatbot-wrapper" style={{ marginTop: "-15px" }}>
        <ChatBotLauncher onOpen={handleLauncherClick} />
        {drawerOpen && !expanded ? (
          <ChatBotDrawer
            messages={displayMessages}
            onExpand={(e) => {
              setExpanded(true);
              setDrawerOpen(false);
              pageViewHandler(e, "AiLoanChatbot");
            }}
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              resetChat();
            }}
            msg={msg}
            setMsg={setMsg}
            onSend={handleSend}
            isTyping={isTyping}
            mode={mode}
            setMode={setMode}
            onModeChange={(m) => changeMode(m, true)}
            streamingMessageId={streamingMessageId}
          />
        ) : (
          ""
        )}

        {expanded && pageState == "AiLoanChatbot" ? (
          <ChatBotExpanded
            messages={displayMessages}
            activeChat={activeChat}
            chats={chats}
            createNewChat={createNewChat}
            loadChat={loadChat}
            msg={msg}
            setMsg={setMsg}
            onSend={handleSend}
            isTyping={isTyping}
            mode={mode}
            handleModeChange={(m) => changeMode(m, true)}
            setFollowUpRef={setFollowUpRef}
            activeChatId={activeChatId}
            hasTyped={hasTyped}
            currentMessages={currentMessages}
            setChats={setChats}
            onBack={(e) => {
              setExpanded(false);
              setDrawerOpen(true);
              pageViewHandler(e, "landingPage");
            }}
            streamingMessageId={streamingMessageId}
          />
        ) : (
          ""
        )}
      </div>
    </>
  );
}
